/**
 * SVC API
 * https://<backend url>/svc/metadata
 *
 * Common resource GET/POST/PUT/PATCH template:

    getFoo (id, params, config) {
        return this.$axios
            .get(`/svc/foos/${id}`, this.getConfig(params, config))
            .catch(this.handleError);
    }

    queryFoos (params, config) {
        return this.$axios
            .post(`/svc/foos/query`, this.getData(params, false), config)
            .catch(this.handleError);
    }

    postFoo (data, config) {
        return this.$axios
            .post(`/svc/foos`, data, config)
            .catch(this.handleError);
    }

    putFoo (id, payload, config) {
        return this.$axios
            .put(`/svc/foos/${id}`, payload, config)
            .catch(this.handleError);
    }

    patchFoo (id, payload, config) {
        return this.$axios
            .patch(`/svc/foos/${id}`, payload, config)
            .catch(this.handleError);
    }
 */
/* eslint no-multiple-empty-lines: [1, { max: 4} ] */
import { logErrorTrace } from '@/helpers';
import { isProdSite } from '@/helpers/deployment';

export default class SVC {

    constructor (ctx) {
        this.$axios = ctx.axios;
        this.$store = ctx.store;
    }

    resetCache () {} // no axios-extensions cache

    handleError (error) {
        // no error trace when request is canceled
        if (error && !error.__CANCEL__) {
            logErrorTrace(error, !isProdSite);
        }
        throw error;
    }

    /** Auth0 ----------------------------------------- */

    getAuth0 (code) {
        return this.$axios
            .get(`/svc/auth/auth0`, {
                // params: {code: code},
                maxRedirects: 0,
                withCredentials: true,
                headers: {
                    Accept: '*/*'
                }
            })
            .catch(() => {
                // maxRedirects does not seem to work.
                // if the request redirects to muusa (and fails),
                // but session is still ok.
            });
    }




    /** Hello and User Profile ------------------------ */

    getHello () {
        return this.$axios
            .get(`/svc/hello`)
            .catch(this.handleError);
    }

    getProfile () {
        return this.$axios
            .get(`/svc/profile`)
            .catch(this.handleError);
    }

    patchProfile (payload) {
        return this.$axios
            .patch(`/svc/profile`, payload)
            .catch(this.handleError);
    }

    /** Profile Item, GET.
     *   - type: saved, liked, followed, shared
     * */
    getProfileItems (type, params, config) {
        return this.$axios
            .get(`/svc/profile/items/${type}`, _.extend({params}, config))
            .catch(this.handleError);
    }

    /** Profile Item, POST query.
     *   - type: saved, liked, followed, shared
     * */
    queryProfileItems (type, params, config) {
        return this.$axios
            .post(`/svc/profile/items/${type}/query`, _.extend({
                EnableFacetedSearch: true,
                UseProfile: 'ViewAll',
                PageSize: 250,
                Template: {
                    PrimaryProperties: true,
                    IndexProperties: true,
                    ObjectProperties: true,
                    IngressProperties: false,
                    ArtworkProperties: true,
                    DetailProperties: false,
                    ImageInfoExtended: true,
                    AuxMediaProperties: false,
                    MakersProperties: 'MakersActorSimple'
                }
            }, params), config)
            .catch(this.handleError);
    }

    /** Profile FeatureArticles
     *  - state: published, preliminary, archived, deleted
     * */

    getProfileFeatureArticles (resourceState, params, config) {
        return this.$axios
            .get(`/svc/profile/featurearticles/${resourceState || ''}`, _.extend({params}, config))
            .catch(this.handleError);
    }

    /** Profile Invites */

    getProfileInvites (params, config) {
        return this.$axios
            .get(`/svc/profile/invites`, _.extend({params}, config))
            .catch(this.handleError);
    }

    postProfileInvite (data, config) {
        return this.$axios
            .post(`/svc/profile/invites`, data, config)
            .catch(this.handleError);
    }

    deleteProfileInvite (id, config) {
        return this.$axios
            .delete(`/svc/profile/invites`, config)
            .catch(this.handleError);
    }

    /** Profile Accounts & Account Subcriptions */

    getMyAccount (accountId, params, config) {
        return this.$axios
            .get(`/svc/profile/accounts/${accountId}`, _.extend({params}, config))
            .catch(this.handleError);
    }

    getMyAccounts (params, config) {
        return this.$axios
            .get(`/svc/profile/accounts`, _.extend({params}, config))
            .catch(this.handleError);
    }

    getMyAccountSubscriptions (accountId, params, config) {
        return this.$axios
            .get(`/svc/profile/accounts/${accountId}/subscriptions`, _.extend({params}, config))
            .catch(this.handleError);
    }

    getMyAccountSubscriptionWorkspaces (accountId, subscriptionId, params, config) {
        return this.$axios
            .get(`/svc/profile/accounts/${accountId}/subscriptions/${subscriptionId}`, _.extend({params}, config))
            .catch(this.handleError);
    }

    /** Profile Subscriptions */

    getMySubscription (subscriptionId, params, config) {
        return this.$axios
            .get(`/svc/profile/subscriptions/${subscriptionId}`, _.extend({params}, config))
            .catch(this.handleError);
    }

    getMySubscriptions (params, config) {
        return this.$axios
            .get(`/svc/profile/subscriptions`, _.extend({params}, config))
            .catch(this.handleError);
    }

    /** Profile Offers */

    getMyOffers (params, config) {
        return this.$axios
            .get(`/svc/profile/offers`, _.extend({params}, config))
            .catch(this.handleError);
    }

    postRequestOffer (data, config) {
        return this.$axios
            .post(`/svc/profile/offers`, data, config)
            .catch(this.handleError);
    }

    postUseOffer (offerId, data, config) {
        return this.$axios
            .post(`/svc/profile/offers/${offerId}`, data, config)
            .catch(this.handleError);
    }




    /** Generic item ---------------------------------- */

    getItem (id, params, config) { // id or slug both work
        return this.$axios
            .get(`/svc/items/${id}`, this.getConfig(params, config))
            .catch(this.handleError);
    }




    /** Generic resource article ---------------------- */

    getResourceArticle (resource, id, params, config) {
        return this.$axios
            .get(`/svc/${resource}/${id}/article`, this.getConfig(params, config))
            .catch(this.handleError);
    }

    putResourceArticle (resource, id, payload) {
        return this.$axios
            .put(`/svc/${resource}/${id}/article`, payload)
            .catch(this.handleError);
    }

    patchResourceArticle (resource, id, payload) {
        return this.$axios
            .patch(`/svc/${resource}/${id}/article`, payload)
            .catch(this.handleError);
    }




    /** ArtImage -------------------------------------- */

    patchArtImage (id, payload) {
        return this.$axios
            .patch(`/svc/artimages/${id}`, payload)
            .catch(this.handleError);
    }




    /** Artwork --------------------------------------- */

    getArtwork (id, params, config) {
        return this.$axios
            .get(`/svc/artworks/${id}`, this.getConfig(params, config))
            .catch(this.handleError);
    }

    queryArtworks (params, config) {
        return this.$axios
            .post('/svc/artworks/query', this.getData(params, true), config)
            .catch(this.handleError);
    }

    patchArtwork (id, payload) {
        return this.$axios
            .patch(`/svc/artworks/${id}`, payload)
            .catch(this.handleError);
    }




    /** Actors ---------------------------------------- */

    getActor (id, params, config) {
        return this.$axios
            .get(`/svc/actors/${id}`, this.getConfig(params, config))
            .catch(this.handleError);
    }

    getActorArtworks (id, params, config) {
        return this.$axios
            .get(`/svc/actors/${id}/artworks`, this.getConfig(params, config))
            .catch(this.handleError);
    }

    /**
     * Actor Artworks query.
     */
    queryActorArtworks (id, params, config) {
        return this.$axios
            .post('/svc/artworks/query', this.getData(_.extend({ProductionCarriedOutById: id}, params), true), config)
            .catch(this.handleError);
    }

    queryActorExhibitions (id, params, config) {
        return this.$axios
            .post('/svc/events/query', this.getData(_.extend({ArtistId: id}, params), true), config)
            .catch(this.handleError);
    }

    queryActors (params, config) {
        return this.$axios
            .post('/svc/actors/query', this.getData(params, false), config)
            .catch(this.handleError);
    }

    patchActor (id, payload) {
        return this.$axios
            .patch(`/svc/actors/${id}`, payload)
            .catch(this.handleError);
    }




    /** Collections ----------------------------------- */

    getCollection (id, params, config) {
        return this.$axios
            .get(`/svc/collections/${id}`, this.getConfig(params, config))
            .catch(this.handleError);
    }

    getCollectionArtworks (id, params, config) {
        return this.$axios
            .get(`/svc/collections/${id}/artworks`, this.getConfig(params, config))
            .catch(this.handleError);
    }

    /**
     * Collection Artworks query.
     */
    queryCollectionArtworks (id, params, config) {
        return this.$axios
            .post('/svc/artworks/query', this.getData(_.extend({CollectionId: id}, params), true), config)
            .catch(this.handleError);
    }

    queryCollections (params, config) {
        return this.$axios
            .post('/svc/collections/query', this.getData(params, false), config)
            .catch(this.handleError);
    }

    patchCollection (id, payload) {
        return this.$axios
            .patch(`/svc/collections/${id}`, payload)
            .catch(this.handleError);
    }




    /** Organizations --------------------------------- */

    getOrganization (id, params, config) {
        return this.$axios
            .get(`/svc/organizations/${id}`, this.getConfig(params, config))
            .catch(this.handleError);
    }

    queryOrganizations (params, config) {
        return this.$axios
            .post('/svc/organizations/query', this.getData(params, false), config)
            .catch(this.handleError);
    }

    patchOrganization (id, payload) {
        return this.$axios
            .patch(`/svc/organizations/${id}`, payload)
            .catch(this.handleError);
    }

    /** Organization Collections */

    getOrganizationCollections (id, params, config) {
        return this.$axios
            .get(`/svc/organizations/${id}/collections`, this.getConfig(params, config))
            .catch(this.handleError);
    }

    /** Organization Artworks query */

    queryOrganizationArtworks (id, params, config) {
        return this.$axios
            .post('/svc/artworks/query', this.getData(_.extend({PublishedKeeperId: id}, params), true), config)
            .catch(this.handleError);
    }


    /** Organization ContactDepartments
     *  NOTE: API calls ContactDepartment as OrganizationContact with contactId
    */

    getOrganizationContactDepartments (organizationId, params, config) {
        return this.$axios
            .get(`/svc/organizations/${organizationId}/departments`, this.getConfig(params, config))
            .catch(this.handleError);
    }

    postOrganizationContactDepartment (organizationId, data, config) {
        return this.$axios
            .post(`/svc/organizations/${organizationId}/departments`, data, config)
            .catch(this.handleError);
    }

    putOrganizationContactDepartment (organizationId, departmentId, payload, config) {
        return this.$axios
            .put(`/svc/organizations/${organizationId}/departments/${departmentId}`, payload, config)
            .catch(this.handleError);
    }

    deleteOrganizationContactDepartment (organizationId, departmentId, config) {
        return this.$axios
            .delete(`/svc/organizations/${organizationId}/departments/${departmentId}`, config)
            .catch(this.handleError);
    }

    /** Organization ContactPersonnels
     *  NOTE: API calls ContactPersonnel as OrganizationContactPersonnelContact with personnelId
    */

    getOrganizationContactPersonnels (organizationId, departmentId, params, config) {
        return this.$axios
            .get(`/svc/organizations/${organizationId}/departments/${departmentId}/personnelcontacts`, this.getConfig(params, config))
            .catch(this.handleError);
    }

    getOrganizationContactPersonnel (organizationId, departmentId, personnelId, params, config) {
        return this.$axios
            .get(`/svc/organizations/${organizationId}/departments/${departmentId}/personnelcontacts/${personnelId}`, this.getConfig(params, config))
            .catch(this.handleError);
    }

    postOrganizationContactPersonnel (organizationId, departmentId, data, config) {
        return this.$axios
            .post(`/svc/organizations/${organizationId}/departments/${departmentId}/personnelcontacts`, data, config)
            .catch(this.handleError);
    }

    putOrganizationContactPersonnel (organizationId, departmentId, personnelId, payload, config) {
        return this.$axios
            .put(`/svc/organizations/${organizationId}/departments/${departmentId}/personnelcontacts/${personnelId}`, payload, config)
            .catch(this.handleError);
    }

    deleteOrganizationContactPersonnel (organizationId, departmentId, personnelId, config) {
        return this.$axios
            .delete(`/svc/organizations/${organizationId}/departments/${departmentId}/personnelcontacts/${personnelId}`, config)
            .catch(this.handleError);
    }

    /** Organization EntryDescriptions */

    getOrganizationEntryDescriptions (organizationId, params, config) {
        return this.$axios
            .get(`/svc/organizations/${organizationId}/entrydescriptions`, this.getConfig(params, config))
            .catch(this.handleError);
    }

    getOrganizationEntryDescription (organizationId, id, params, config) {
        return this.$axios
            .get(`/svc/organizations/${organizationId}/entrydescriptions/${id}`, this.getConfig(params, config))
            .catch(this.handleError);
    }

    queryOrganizationEntryDescriptions (organizationId, params, config) {
        return this.$axios
            .post(`/svc/organizations/${organizationId}/entrydescriptions/query`, this.getData(params, false), config)
            .catch(this.handleError);
    }

    postOrganizationEntryDescription (organizationId, data, config) {
        return this.$axios
            .post(`/svc/organizations/${organizationId}/entrydescriptions`, data, config)
            .catch(this.handleError);
    }

    putOrganizationEntryDescription (organizationId, id, payload, config) {
        return this.$axios
            .put(`/svc/organizations/${organizationId}/entrydescriptions/${id}`, payload, config)
            .catch(this.handleError);
    }

    patchOrganizationEntryDescription (organizationId, id, payload, config) {
        return this.$axios
            .patch(`/svc/organizations/${organizationId}/entrydescriptions/${id}`, payload, config)
            .catch(this.handleError);
    }

    deleteOrganizationEntryDescription (organizationId, id, config) {
        return this.$axios
            .delete(`/svc/organizations/${organizationId}/entrydescriptions/${id}`, config)
            .catch(this.handleError);
    }




    /** Feature Articles ------------------------------ */

    getFeatureArticle (id, params, config) {
        return this.$axios
            .get(`/svc/featurearticles/${id}`, this.getConfig(params, config))
            .catch(this.handleError);
    }

    getFeatureArticleRevision (id, locale, publicationState, revision, params, config) {
        const _locale = locale || 'all';
        return this.$axios
            .get(`/svc/featurearticles/${id}/${_locale}/${publicationState}/${revision}`, this.getConfig(params, config))
            .catch(this.handleError);
    }

    queryFeatureArticles (params, config) {
        return this.$axios
            .post('/svc/featurearticles/query', this.getData(params, false), config)
            .catch(this.handleError);
    }

    postFeatureArticle (data, newResourceState, config) {
        return this.$axios
            .post(`/svc/featurearticles/${newResourceState}`, data, config)
            .catch(this.handleError);
    }

    putFeatureArticle (id, payload, config) {
        return this.$axios
            .put(`/svc/featurearticles/${id}`, payload, config)
            .catch(this.handleError);
    }

    patchFeatureArticle (id, payload, config) {
        return this.$axios
            .patch(`/svc/featurearticles/${id}`, payload, config)
            .catch(this.handleError);
    }

    deleteFeatureArticle (id, params, config) {
        const _params = _.extend({UseProfile: 'EditAll'}, params);
        return this.$axios
            .delete(`/svc/featurearticles/${id}`, this.getConfig(_params, config))
            .catch(this.handleError);
    }

    /** Linked FeatureArticles */

    getLinkedFeatureArticles (resource, id, params, config) {
        return this.$axios
            .get(`/svc/${resource}/${id}/featurearticles/linked`, this.getConfig(params, config))
            .catch(this.handleError);
    }




    /** Revisions ------------------------------------- */

    getRevisions (resource, id, params, config) {
        return this.$axios
            .get(`/svc/${resource}/${id}/revisions`, this.getConfig(params, config))
            .catch(this.handleError);
    }

    postRevision (resource, id, locale, revision, data, config) {
        const _locale = locale || 'all';
        return this.$axios
            .post(`/svc/${resource}/${id}/revisions/${_locale}/${revision}`, data, config)
            .catch(this.handleError);
    }




    /** Events ---------------------------------------- */

    getEvent (id, params, config) {
        return this.$axios
            .get(`/svc/events/${id}`, this.getConfig(params, config))
            .catch(this.handleError);
    }

    queryEvents (params, config) {
        return this.$axios
            .post('/svc/events/query', this.getData(params, false), config)
            .catch(this.handleError);
    }

    postEvent (data, config) {
        return this.$axios
            .post(`/svc/events`, data, config)
            .catch(this.handleError);
    }

    putEvent (id, payload, config) {
        return this.$axios
            .put(`/svc/events/${id}`, payload, config)
            .catch(this.handleError);
    }

    patchEvent (id, payload, config) {
        return this.$axios
            .patch(`/svc/events/${id}`, payload, config)
            .catch(this.handleError);
    }

    /** Event Artworks */

    getEventArtworks (id, params, config) {
        return this.$axios
            .get(`/svc/events/${id}/artworks`, this.getConfig(params, config))
            .catch(this.handleError);
    }

    /** Event EntryDescription */

    getEventEntryDescription (eventId, params, config) {
        return this.$axios
            .get(`/svc/events/${eventId}/entrydescription`, this.getConfig(params, config))
            .catch(this.handleError);
    }

    putEventEntryDescription (eventId, payload, config) {
        return this.$axios
            .put(`/svc/events/${eventId}/entrydescription`, payload, config)
            .catch(this.handleError);
    }

    patchEventEntryDescription (eventId, payload, config) {
        return this.$axios
            .patch(`/svc/events/${eventId}/entrydescription`, payload, config)
            .catch(this.handleError);
    }

    deleteEventEntryDescription (eventId, config) {
        return this.$axios
            .delete(`/svc/events/${eventId}/entrydescription`, config)
            .catch(this.handleError);
    }




    /** Workspaces ------------------------------------ */

    getWorkspace (id, params, config) {
        const _params = _.extend({UseProfile: 'All'}, params);
        return this.$axios
            .get(`/svc/workspaces/${id}`, this.getConfig(_params, config))
            .catch(this.handleError);
    }

    getWorkspaces (params, config) {
        const _params = _.extend({UseProfile: 'All'}, params);
        return this.$axios
            .get(`/svc/workspaces`, this.getConfig(_params, config))
            .catch(this.handleError);
    }

    getWorkspaceArtworks (id, params) {
        return this.$axios
            .get(`/svc/workspaces/${id}/artworks`, params)
            .catch(this.handleError);
    }

    postWorkspaceArtwork (id, data, config) {
        return this.$axios
            .post(`/svc/workspaces/${id}/artworks`, data, config)
            .catch(this.handleError);
    }




    /** Pages (front page) ---------------------------- */

    getPages (route, params, config) {
        // UseProfile should not be set here
        const _config = this.getConfig(params, config);
        delete _config.UseProfile;
        return this.$axios
            .get(`/svc/pages/${route ? '/' + route : ''}`, _config)
            .catch(this.handleError);
    }



    /** Items ----------------------------------------- */

    queryItems (params, config) {
        return this.$axios
            .post(`/svc/items/query`, this.getData(params, false), config)
            .catch(this.handleError);
    }

    /** Recommendations */

    getRecommendations (contentFilter, params, config) {
        return this.$axios
            .get(`/svc/items/recommend?ContentClassesFilter=${contentFilter}`, this.getConfig(params, config))
            .catch(this.handleError);
    }




    /** Social actions -------------------------------- */

    /**
     * PUT operation.
     *  - follow, unfollow
     *  - heart, unheart
     *  - save, unsave
     *  - share
     *
     * eg. /svc/artworks/1234/log/heart
     */
    putOperation (resource, id, operation) {
        return this.$axios
            .put(`/svc/${resource}/${id}/log/${operation}`)
            .catch(this.handleError);
    }




    /** Tags ------------------------------------------ */

    getTag (id, params, config) {
        return this.$axios
            .get(`/svc/tags/${id}`, this.getConfig(params, config))
            .catch(this.handleError);
    }

    putTag (resource, id, payload) {
        return this.$axios
            .put(`/svc/${resource}/${id}/tag`, payload)
            .catch(this.handleError);
    }

    queryTags (params, config) {
        return this.$axios
            .post(`/svc/tags/query`, this.getData(params, false), config)
            .catch(this.handleError);
    }




    /** Shared ---------------------------------------- */

    queryShared (params, config) {
        return this.$axios
            .post(`/svc/shares/query`, this.getData(params, false), config)
            .catch(this.handleError);
    }




    /** Videos ---------------------------------------- */

    getVideo (id, params, config) {
        return this.$axios
            .get(`/svc/artvideos/${id}`, this.getConfig(params, config))
            .catch(this.handleError);
    }

    queryVideos (params, config) {
        return this.$axios
            .post('/svc/artvideos/query', this.getData(params, false), config)
            .catch(this.handleError);
    }

    patchVideo (id, payload) {
        return this.$axios
            .patch(`/svc/artvideos/${id}`, payload)
            .catch(this.handleError);
    }




    /** Audios ---------------------------------------- */

    getAudio (id, params, config) {
        return this.$axios
            .get(`/svc/artaudios/${id}`, this.getConfig(params, config))
            .catch(this.handleError);
    }

    getAudioInfo (id, params, config) {
        return this.$axios
            .get(`/svc/artaudios/${id}/info`, this.getConfig(params, config))
            .catch(this.handleError);
    }

    queryAudios (params, config) {
        return this.$axios
            .post('/svc/artaudios/query', this.getData(params, false), config)
            .catch(this.handleError);
    }

    patchAudio (id, payload) {
        return this.$axios
            .patch(`/svc/artaudios/${id}`, payload)
            .catch(this.handleError);
    }




    /** File Archives & Folders ----------------------- */

    getFileLibraries (params, config) {
        const _params = _.extend({UseProfile: 'All'}, params);
        return this.$axios
            .get(`/svc/filelibraries`, this.getConfig(_params, config))
            .catch(this.handleError);
    }

    getFileLibrary (fileLibraryId, params, config) {
        const _params = _.extend({UseProfile: 'All'}, params);
        return this.$axios
            .get(`/svc/filelibraries/${fileLibraryId}`, this.getConfig(_params, config))
            .catch(this.handleError);
    }

    getFileLibraryFolder (fileLibraryId, folderId, subFolders, params, config) {
        const _params = _.extend({UseProfile: 'All'}, params);
        return this.$axios
            .get(`/svc/filelibraries/${fileLibraryId}/${folderId}/${subFolders || ''}`, this.getConfig(_params, config))
            .catch(this.handleError);
    }

    updateFileLibraryFolder (fileLibraryId, folderId, subFolders, data, config) {
        return this.$axios
            .post(`/svc/filelibraries/${fileLibraryId}/${folderId}/${subFolders || ''}`, data, config)
            .catch(this.handleError);
    }




    /** OSM - Open Street Map objects ----------------- */

    osmAddressSearch (data, config) {
        return this.$axios
            .post(`/svc/osmobjects/lookup`, data, config)
            .catch(this.handleError);
    }

    osmForwardSearch (queryString, data, config) {
        return this.$axios
            .post(`/svc/osmobjects/search/${queryString}`, data, config)
            .catch(this.handleError);
    }

    osmReverseSearch (data, config) {
        return this.$axios
            .post(`/svc/osmobjects/reverse`, data, config)
            .catch(this.handleError);
    }




    /** LawDocs: Terms of Service --------------------- */

    lawDocsTermsOfService () {
        return this.$axios
            .get('/svc/lawdocs/tos', {params: this.getPreferredLocaleParams()})
            .catch(this.handleError);
    }

    /** LawDocs: Privacy Policy ----------------------- */

    lawDocsPrivacyPolicy () {
        return this.$axios
            .get('/svc/lawdocs/privacy', {params: this.getPreferredLocaleParams()})
            .catch(this.handleError);
    }



    /** Helpers --------------------------------------- */

    /**
    * The "Template" parameter specifies the "fatness" of query response.
    *
    *    IndexProperties    - boolean -
    *    ObjectProperties   - boolean - includes fields not in IndexProperties
    *    Dimensions         - boolean -
    *    MakersProperties   - string  - ['NoMakers', 'Makers', 'MakersActorSimple', 'MakersActorFull']
    *    ImageInfoExtended  - boolean -
    *    AuxMedia           - boolean -
    *    Collections        - boolean -
    */
    getTemplateParams (params) {
        return {
            Template: _.extend({
                IndexProperties: true,
                ObjectProperties: false,
                Dimensions: false,
                MakersProperties: 'NoMakers',
                ImageInfoExtended: false,
                AuxMedia: false,
                Collections: false
            }, params)
        };
    }

    /**
    * The "UseProfile" request param to switch view scope. "View" prefix is optional.
    *
    *    AnonymousOnly
    *    RegisteredOnly
    *    ProRegisteredOnly
    *    PublicOnly (= includes all of the above)
    *    PrivateOnly
    *    All
    *
    *    ViewAll (= All)
    *    ViewPublicOnly (= PublicOnly)
    *
    * With combination of "Edit" prefix is in edit mode.
    *
    *    EditPublicOnly
    *    EditAll
    *    ... etc
    *
    */
    getUseProfileParams () {
        let useProfile = this.$store.getters['ux/getUseProfile'];
        if (this.$store.getters['ux/getEditMode']) {
            // support ViewPublicOnly and PublicOnly
            if (useProfile.match('View')) {
                useProfile = useProfile.replace('View', 'Edit');
            }
            else if (!useProfile.match('Edit')) {
                useProfile = 'Edit' + useProfile;
            }
        }
        return useProfile ? {UseProfile: useProfile} : {};
    }

    /**
    * Preferred locale.
    *    "en", "fi", etc
    */
    getPreferredLocaleParams () {
        const locale = this.$store.getters['ux/getLocale'];
        return {PreferredLocale: locale};
    }

    stringifyQueryParams (params) {
        // "stringify" without spaces
        const values = _.map(Object.keys(params), (key) => {
            return `${key}:${params[key]}`;
        });
        return '{' + values.join(',') + '}';
    }

    /**
    * Merge GET params and axios config.
    */
    getConfig (params, config) {
        const _params = _.extend(
            this.getUseProfileParams(),
            this.getPreferredLocaleParams(),
            params);
        return _.extend({params: _params}, config);
    }

    /**
    * Merge POST/PUT query params with the defaults.
    */
    getData (params, includeTemplate) {
        let data = _.extend(
            {
                AutoWildcards: false,
                EnableFacetedSearch: false
            },
            this.getUseProfileParams(),
            this.getPreferredLocaleParams(),
            params
        );
        if (includeTemplate) {
            data = _.extend(data, this.getTemplateParams(params && params.Template));
        }
        return data;
    }

}
